import { MapEmbed } from "@/components/MapEmbed";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SALON_SERVICE_LINKS } from "@/data/localServiceLinks";
import { BarberGrid } from "@/components/BarberGrid";
import { SALON_BARBERARE, salonBarberLine } from "@/data/barbers";
import { Button } from "@/components/ui/button";
import { salonBySlug } from "@/data/salons";
import { ReviewsBlock } from "@/components/ReviewsBlock";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import { breadcrumbSchema, faqSchema, jsonLd, localBusinessSchema, SITE_URL, absUrl } from "@/lib/seo";
import { reviewsForSalon } from "@/data/reviews";
import arningeUtanfor1200 from "@/assets/opt/arninge-utanfor-1200.webp";
import arningeUtanfor2800 from "@/assets/opt/arninge-utanfor-2-800.webp";
import arningeInne800 from "@/assets/opt/arninge-inne-800.webp";
import { withBookingUtm } from "@/lib/booking";
import { PRICE } from "@/data/pricing";
const salon = salonBySlug["arninge"];
const BOOK_URL = withBookingUtm("https://www.bokadirekt.se/places/tailors-barber-arninge-ica-maxi-huset--61598");
const MAPS_LINK = "https://maps.app.goo.gl/fmmgTSmE3ezxKG2VA";
const MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent("Kundvägen 1D Täby")}&output=embed`;

const services = [
  ["Herrklippning", "450 kr"],
  ["Taper Fade / Skin Fade", "450 kr"],
  ["Skäggtrimning", "380 kr"],
  ["Herrklippning + Skäggtrimning", "680 kr"],
  ["Barnklippning (upp till 10 år)", "380 kr"],
  ["Seniorklippning (mån–tors 10–14)", "270 kr"],
];

const faq = [
  { q: "Var ligger Tailor's Barber i Arninge?", a: "Vi finns i ICA Maxi Huset i Arninge, Kundvägen 1D, Täby. Salongen ligger inne i galleriadelen vid ICA Maxi." },
  { q: "Är Tailor's Barber Arninge öppen på helgen?", a: "Ja, vi har öppet lördag 10:00–18:00 och söndag 10:00–18:00. Vardagar 10:00–19:00." },
  { q: "Hur mycket kostar herrklippning i Arninge?", a: "Herrklippning kostar 450 kr. Skäggtrimning 380 kr. Kombination hår + skägg 680 kr. Barnklippning 380 kr." },
  { q: "Kan jag komma som drop-in till Arninge?", a: "Ja, drop-in är välkommet i mån av tid. Vill du vara säker på att få en tid rekommenderar vi att du bokar online via BokaDirekt." },
  { q: "Hur tar jag mig till Arninge utan bil?", a: "Arninge nås med buss 619 från Täby Centrum. Med bil finns parkering direkt vid ICA Maxi Arninge." },
  { q: "Erbjuder ni skin fade i Arninge?", a: "Ja, vi erbjuder taper fade och skin fade i Arninge. Skin fade är en av våra mest bokade tjänster – razor-sharp övergång mot skinnet." },
];

const localBiz = {
  ...localBusinessSchema(salon, reviewsForSalon(salon.slug)),
  description: "Barbersalong i ICA Maxi Arninge, Täby. Herrklippning, taper fade, skin fade och skäggtrimning. Modern salong med yrkesskickliga barberare.",
  containedInPlace: {
    "@type": "ShoppingCenter",
    name: "ICA Maxi Huset Arninge",
    address: "Kundvägen 1D, Täby",
  },
  hasMap: MAPS_LINK,
  priceRange: "$$",
  telephone: "+468371156",
  parentOrganization: { "@type": "Organization", name: "Tailor's Barber", url: SITE_URL },
};

export const Route = createFileRoute("/barberare-stockholm-arninge")({
  head: () => ({
    meta: [
      { title: "Frisör Arninge: herrklippning 450 kr | Tailor's Barber" },
      { name: "description", content: "Frisör Arninge i ICA Maxi Huset. Herrklippning 450 kr, skin fade, taper fade och skägg. Drop-in välkommet. Boka online på BokaDirekt." },
      { property: "og:title", content: "Frisör Arninge: herrklippning 450 kr | Tailor's Barber" },
      { property: "og:description", content: "Frisör Arninge i ICA Maxi Huset. Herrklippning 450 kr, skin fade, taper fade och skägg. Boka online." },
      { property: "og:url", content: absUrl(`${SITE_URL}/barberare-stockholm-arninge/`) },
      { property: "og:image", content: absUrl(salon.image) },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: absUrl(`${SITE_URL}/barberare-stockholm-arninge/`) },
      { rel: "preload", as: "image", href: arningeUtanfor1200, fetchpriority: "high" },
    ],

    scripts: [
      jsonLd(localBiz),
      jsonLd(faqSchema(faq)),
      jsonLd(breadcrumbSchema([
        { name: "Hem", url: "/" },
        { name: "Arninge", url: "/barberare-stockholm-arninge/" },
      ])),
    ],
  }),
  component: ArningePage,
});

function ArningePage() {
  return (
    <SiteShell>
      <section className="container-page pt-6 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Arninge</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow">Frisör i Arninge</div>
            <h1 className="mt-3 text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
              Frisör Arninge: Tailor's Barber i ICA Maxi Huset
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-prose leading-relaxed">
              Frisör Arninge hos Tailor's Barber finns i ICA Maxi Huset, en modern, ljus salong med
              yrkesskickliga barberare. Perfekt för dig som vill kombinera klippning med shopping eller
               ärenden. Drop-in välkommen i mån av plats, onlinebokning rekommenderas. Herrklippning {PRICE.herrklippning} kr, skäggtrimning {PRICE.skaggtrimning} kr, hår och skägg {PRICE.klippningSkagg} kr.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/70">
              <SalonRatingLines salon="arninge" />
              <span>📍 Kundvägen 1D, Täby (Arninge)</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <Button asChild size="lg">
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online</a>
              </Button>
              <span className="text-sm text-foreground/70">Drop-in välkommen i mån av tid</span>
            </div>
          </div>
          <div className="grid grid-cols-3 grid-rows-2 gap-3 aspect-[4/3]">
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-muted shadow-soft">
              <img src={arningeUtanfor1200} alt="Frisör Arninge, Tailor's Barber i ICA Maxi Huset" width={1200} height={1601} fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img src={arningeUtanfor2800} alt="Ingången till Tailor's Barber, frisör Arninge, med upplyst skylt" width={800} height={1067} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img src={arningeInne800} alt="Interiör med produktdisk och väntsoffor hos Tailor's Barber, frisör Arninge" width={800} height={1070} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>

          </div>
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="container-page py-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="font-medium">Öppettider Arninge:</span>
          <span><strong>Mån–Fre</strong> 10:00–19:00</span>
          <span><strong>Lör</strong> 10:00–18:00</span>
          <span><strong>Sön</strong> 10:00–18:00</span>
        </div>
      </section>




      <section className="container-page py-12">
        <h2 className="text-2xl md:text-3xl">Frisör Arninge Centrum med barberare</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Vår Arninge-salong erbjuder samma höga standard som resten av kedjan: utbildade barberare,
          noggrann konsultation och konsekventa resultat. Frisör Arninge är belägen i galleriadelen
          vid ICA Maxi Huset och erbjuder:
        </p>
        <ul className="mt-5 grid gap-3 md:grid-cols-2 max-w-3xl">
          <li className="card-elev p-5"><strong>Lättillgängligt läge</strong> – inne i ICA Maxi Arninge, stor parkeringsplats</li>
          <li className="card-elev p-5"><strong>Yrkesskickliga barberare</strong> – specialiserade på herrklippning och fade</li>
          <li className="card-elev p-5"><strong>Öppet alla dagar</strong> – vardagar till 19:00, helger 10:00–18:00</li>
          <li className="card-elev p-5">
            <SalonRatingLines salon="arninge" />
            <span className="mt-1 block">Konsekventa resultat varje gång</span>
          </li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-5">Tjänster och priser – Arninge</h2>
        <div className="card-elev overflow-hidden max-w-3xl">
          <table className="w-full text-left">
            <tbody>
              {services.map(([name, price], i) => (
                <tr key={i} className={i > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4">{name}</td>
                  <td className="px-5 py-4 text-right font-medium whitespace-nowrap">{price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Se hela vår <Link to="/priser/" className="underline">prislista</Link>.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Tjänster vi erbjuder i Arninge</h2>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/herrklippning/" className="underline">Herrklippning</Link> – 450 kr, ca 30–40 min</li>
          <li><Link to="/taper-fade/" className="underline">Taper Fade</Link> – gradvis övergång mot sidorna</li>
          <li><Link to="/skin-fade-arninge/" className="underline">Skin Fade Arninge</Link> – razor-sharp precision</li>
          <li><Link to="/skaggtrimning/" className="underline">Skäggtrimning</Link> – 380 kr</li>
          <li><Link to="/herrklippning-skaggtrimning/" className="underline">Hår & Skägg-paket</Link> – komplett behandling, 680 kr</li>
          <li><Link to="/barnklippning-arninge/" className="underline">Barnklippning Arninge</Link> – för barn upp till 10 år, 380 kr</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-6">Teamet på Arninge</h2>
        <p className="mt-0 mb-6 text-foreground/80">{salonBarberLine("arninge")}</p>
        <BarberGrid barbers={SALON_BARBERARE["arninge"].barbers} salonName="Arninge" />
      </section>

      <ReviewsBlock salon="arninge" heading="Vad våra kunder säger om Arninge" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Hitta till oss – Barberare i Arninge</h2>
        <div className="grid md:grid-cols-2 gap-8 mt-6">
          <div>
            <p className="text-foreground/85">
              <strong>Adress:</strong>{" "}
              <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="underline">
                Kundvägen 1D, 187 66 Täby (ICA Maxi Huset, Arninge)
              </a>
            </p>
            <h3 className="mt-5 text-lg font-medium">Kollektivtrafik & parkering</h3>
            <ul className="mt-2 space-y-1 text-foreground/80">
              <li><strong>Buss:</strong> Linje 619 stannar vid Arninge Centrum</li>
              <li><strong>Bil:</strong> Gratis parkering direkt vid ICA Maxi Arninge</li>
            </ul>
            <h3 className="mt-5 text-lg font-medium">Öppettider</h3>
            <ul className="mt-2 space-y-1 text-foreground/80">
              <li><strong>Mån–Fre:</strong> 10:00–19:00</li>
              <li><strong>Lör:</strong> 10:00–18:00</li>
              <li><strong>Sön:</strong> 10:00–18:00</li>
            </ul>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted shadow-soft min-h-[280px]">
            <MapEmbed src={MAPS_EMBED} title="Karta över Tailor's Barber Arninge" href={MAPS_LINK} />
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="card-elev p-8 md:p-12 bg-ink text-cream">
          <div className="eyebrow text-brass">Boka eller drop-in</div>
          <h2 className="mt-3 text-3xl md:text-4xl text-cream">Boka barberare i Arninge</h2>
          <p className="mt-4 max-w-prose text-cream/80">
            Boka din tid online – välj barberare och klockslag som passar dig. Drop-in välkommen i mån av plats. Så fungerar <Link to="/drop-in-barber-stockholm/" className="underline">drop-in hos barberare i Stockholm</Link>.
          </p>
          <div className="mt-6">
            <Button asChild size="lg" className="bg-brass text-ink hover:bg-brass/90">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online – Arninge</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Frisör eller barberare i Arninge?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En frisör är utbildad för hela bredden: dam och herr, färgning, längre frisyrer och
          permanent. En barberare har smalnat av och arbetar i stort sett bara med herrhår och skägg.
          Verktygen skiljer sig också: hos oss används maskin och rakkniv minst lika mycket som saxen.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Därför är barbershopen oftast rätt val när klippningen är kort, när övergången ska sitta rent
          eller när skägget ska formas efter ansiktet. Söker du en frisör i Arninge för färg eller en
          längre frisyr säger vi hellre det direkt, men ska håret vara kort, fade:at eller skägget
          trimmat är du på rätt plats hos oss vid ICA Maxi.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Fler Tailor's Barber-salonger</h2>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/barberare-stockholm-taby/" className="underline">Täby Centrum</Link> – Westfield Täby Centrum, flaggskeppssalong</li>
          <li><Link to="/barberare-stockholm-akersberga/" className="underline">Åkersberga</Link> – ICA Maxi Åkersberga</li>
          <li><Link to="/barberare-stockholm-ahlens-city/" className="underline">Åhléns City Stockholm</Link> – 1 minut från T-Centralen</li>
        </ul>
      </section>

      <ContextLinkBlock
        heading="Tjänster i Arninge"
        links={SALON_SERVICE_LINKS["arninge"]!.links}
      />

      <Faq items={faq} heading="Vanliga frågor – Barberare Arninge" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Redo att boka i Arninge?</h2>
          <p className="mt-3 text-foreground/80">ICA Maxi Arninge, Täby. Boka online eller drop-in i mån av tid.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka online</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
